import { useEffect } from "react";
import { ArrowLeft, Shield } from "lucide-react";

const Block = ({ title, children }) => (
  <section className="mb-8">
    <h2 className="text-xl sm:text-2xl font-black text-[#0A1E3F] dark:text-white mb-3">{title}</h2>
    <div className="text-slate-600 dark:text-slate-300 text-sm sm:text-base leading-relaxed space-y-3">{children}</div>
  </section>
);

const PrivacidadPage = () => {
  useEffect(() => {
    document.title = "Política de Privacidad · Club Viajemos";
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white dark:bg-[#0A1E3F]" style={{ fontFamily: "'Inter', system-ui, sans-serif" }} data-testid="privacidad-page">
      <div className="bg-[#0A1E3F] text-white py-2 text-center text-xs sm:text-sm font-bold tracking-wider">
        <Shield className="inline h-3.5 w-3.5 mr-1.5 mb-0.5" />
        POLÍTICA DE PRIVACIDAD · CLUB VIAJEMOS
      </div>

      <div className="max-w-3xl mx-auto px-5 py-10">
        <a href="/" data-testid="privacidad-back" className="inline-flex items-center gap-2 text-[#45A0EE] hover:underline text-sm font-semibold mb-8">
          <ArrowLeft className="h-4 w-4" /> Volver al inicio
        </a>

        <h1 className="text-3xl sm:text-4xl font-black text-[#0A1E3F] dark:text-white mb-2">Política de Privacidad</h1>
        <p className="text-slate-500 dark:text-slate-400 text-sm mb-10">Última actualización: junio de 2026</p>

        <Block title="1. Introducción">
          <p>En Club Viajemos ("nosotros", "nuestro" o "la empresa") valoramos y respetamos tu privacidad. Esta Política de Privacidad describe cómo recopilamos, usamos, almacenamos y protegemos tu información personal cuando visitas nuestro sitio web <b>www.clubviajemos.com</b> o interactúas con nuestros servicios.</p>
        </Block>

        <Block title="2. Información que recopilamos">
          <p>Podemos recopilar la siguiente información cuando la proporcionas voluntariamente a través de nuestros formularios, chat o canales de contacto:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Nombre completo</li>
            <li>Dirección de correo electrónico</li>
            <li>Número de teléfono / WhatsApp</li>
            <li>País y ciudad de residencia</li>
            <li>Información sobre tus intereses en nuestros productos y servicios de viaje</li>
          </ul>
        </Block>

        <Block title="3. Cómo usamos tu información">
          <p>Utilizamos la información recopilada para:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>Responder tus consultas y brindarte soporte.</li>
            <li>Enviarte información sobre productos, cursos, promociones y oportunidades de negocio.</li>
            <li>Procesar tus solicitudes de cotización, compra o suscripción.</li>
            <li>Mejorar nuestros servicios y la experiencia en el sitio web.</li>
            <li>Comunicarnos contigo por correo electrónico, teléfono o mensajes de texto (SMS), únicamente cuando lo hayas autorizado.</li>
          </ul>
        </Block>

        <Block title="4. Mensajes de texto (SMS) y consentimiento móvil">
          <p><b>No compartimos ni vendemos la información de tu número de teléfono móvil ni tu consentimiento SMS con terceros ni afiliados con fines de marketing o promocionales.</b></p>
          <p>Cuando aceptas recibir mensajes SMS, la información recopilada se usa exclusivamente para comunicarnos contigo respecto a nuestros servicios. Aplican las siguientes condiciones:</p>
          <ul className="list-disc pl-5 space-y-1">
            <li>La frecuencia de los mensajes puede variar.</li>
            <li>Pueden aplicar tarifas de mensajes y datos de tu operador móvil.</li>
            <li>Puedes cancelar tu suscripción en cualquier momento respondiendo <b>STOP</b> (o <b>ALTO</b>).</li>
            <li>Para obtener ayuda, responde <b>HELP</b> (o <b>AYUDA</b>) o contáctanos a info@clubviajemos.com.</li>
          </ul>
          <p>Las categorías de datos móviles no se comparten con terceros para ningún propósito. La información obtenida como parte del programa de mensajería SMS no será compartida con terceros.</p>
        </Block>

        <Block title="5. Compartir información con terceros">
          <p>No vendemos, alquilamos ni comercializamos tu información personal. Podemos compartir datos únicamente con proveedores de servicios de confianza que nos ayudan a operar (por ejemplo, plataformas de pago, correo o mensajería), y solo en la medida necesaria para prestarte el servicio. Estos proveedores están obligados a proteger tu información.</p>
        </Block>

        <Block title="6. Seguridad de los datos">
          <p>Implementamos medidas técnicas y organizativas razonables para proteger tu información personal contra accesos no autorizados, pérdida o divulgación indebida.</p>
        </Block>

        <Block title="7. Tus derechos">
          <p>Tienes derecho a acceder, corregir o solicitar la eliminación de tu información personal, así como a retirar tu consentimiento en cualquier momento. Para ejercer estos derechos, escríbenos a info@clubviajemos.com.</p>
        </Block>

        <Block title="8. Cambios a esta política">
          <p>Podemos actualizar esta Política de Privacidad periódicamente. Publicaremos cualquier cambio en esta página con su fecha de actualización.</p>
        </Block>

        <Block title="9. Contacto">
          <p>Si tienes preguntas sobre esta Política de Privacidad, contáctanos:</p>
          <ul className="list-none space-y-1">
            <li><b>Correo:</b> info@clubviajemos.com</li>
            <li><b>Teléfono / WhatsApp:</b> +1 (954) 805-9212</li>
            <li><b>Sitio web:</b> www.clubviajemos.com</li>
          </ul>
        </Block>

        <a href="/terminos" data-testid="privacidad-to-terminos" className="text-[#45A0EE] hover:underline text-sm font-semibold">Ver también nuestros Términos de Servicio →</a>
      </div>
    </div>
  );
};

export default PrivacidadPage;
